import React, { useState, useEffect, useMemo } from 'react';
import Api from '../../api/global.service';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import CardContainer from '../../components/card/card-container.jsx';
import BlockUi from 'react-block-ui';
import GoogleMapReact from 'google-map-react';
import env from './../../environment.js';
import FormSelectMaterial from '../../nuup/componentes/Forms/FormSelectMaterial.jsx';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Swal from 'sweetalert2';
import useCroquisPuntosHook from '../../hooks/useCroquisPuntosHook.jsx';
import useGeoPuntosLayer, {
  getGeoPuntoPositions,
  getGeometryPositions,
  getPuntoConfig,
  getPolygonFill,
  LINE_STYLES,
  OTRO_PUNTO_DEFAULT_COLOR,
  isCustomPuntoColor,
} from '../../hooks/useGeoPuntosLayer.jsx';
import NorthIcon from '../../components/map/north-icon.jsx';
import MapRecenterButton from '../../components/map/map-recenter-button.jsx';
import SimbologiaColorPicker, {
  buildUsedColors,
  getColoresRecientes,
  addColorReciente,
  normalizeHex,
} from '../../components/map/simbologia-color-picker.jsx';
import { LineSwatch, AreaSwatch, TextSwatch, PointSwatch } from '../../components/map/simbologia-legend.jsx';
import { useCoopConfig } from '../../components/content/coop-config.jsx';
import { CircleMarker, PillMarker, isValidLat, isValidLng, getParcelaPosition, fitMapToPositions } from './padron-mapas-list.js';

const TIPO_OPTIONS = [
  { value: 'punto', label: 'Punto', icon: 'fa-map-marker-alt' },
  { value: 'texto', label: 'Texto', icon: 'fa-font' },
  { value: 'linea', label: 'Línea', icon: 'fa-chart-line' },
  { value: 'area', label: 'Polígono', icon: 'fa-draw-polygon' },
];

const FORMA_LINEA_OPTIONS = [
  { value: 'solid', label: 'Sólida' },
  { value: 'dashed', label: 'Discontinua' },
  { value: 'dotted', label: 'Punteada' },
  { value: 'dashdot', label: 'Guión-punto' },
];

const NIVEL_CONFIG = {
  cooperativa: { label: 'Cooperativa', apiPath: 'cooperativas_simbologia' },
  localidad: { label: 'Comunidad', apiPath: 'localidades_simbologia' },
  cuenta: { label: 'Productor', apiPath: 'cuentas_simbologia' },
};

const NIVEL_FILTRO_CATALOGO = { cooperativa: 'cooperativa', localidad: 'comunidad', cuenta: 'parcela' };
const NOMBRE_OTRO = 'Otro';
const DEFAULT_MAP_CENTER = { lat: 17.040386, lng: -91.842369 };

const tipoButtonStyle = (isActive, disabled = false) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '6px 16px',
  borderRadius: 8,
  border: `1px solid ${isActive ? env.colors.primary_300 : env.colors.neutral_100}`,
  background: isActive ? env.colors.primary_300 : disabled ? '#f5f5f5' : 'white',
  color: isActive ? 'white' : disabled ? env.colors.neutral_300 : env.colors.neutral_700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 13,
  fontWeight: isActive ? 600 : 400,
});

const ReferenceLegend = ({ title, items, panTo, show, onToggleShow }) => {
  if (!items.length) return null;
  return (
    <div style={{ border: '1px solid #c8d1a0', borderRadius: 8, padding: '12px 16px', backgroundColor: '#f9faf4', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: show ? 10 : 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#4A6700', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          {title} ({items.length})
        </div>
        <button type="button" onClick={onToggleShow} className="btn btn-parcela-action btn-sm">
          <i className={`fa ${show ? 'fa-eye-slash' : 'fa-eye'} mr-2`} aria-hidden="true" />
          {show ? 'Ocultar' : 'Mostrar'}
        </button>
      </div>
      {show && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', columnGap: 16, rowGap: 10 }}>
          {items.map((item, i) => (
            <div
              key={i}
              onClick={() => item.pos && panTo(item.pos)}
              title={item.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                cursor: item.pos ? 'pointer' : 'default',
                fontSize: 12,
                minWidth: 0,
              }}>
              {item.badge}
              <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PadronMapasEditSimbologia = ({ nivel }) => {
  const { ownerUuid, itemUuid } = useParams();
  const history = useHistory();
  const location = useLocation();
  const coopConfig = useCoopConfig();
  const isEditMode = !!itemUuid;
  const nivelConfig = NIVEL_CONFIG[nivel];
  const anchor = location.state?.center || DEFAULT_MAP_CENTER;
  const anchorZoom = location.state?.zoom || 16;

  const nivelDisplay = useMemo(() => {
    const subtitleInstructivo =
      'ℹ️ Agrega simbología eligiendo una figura (punto, línea, polígono, etc.), indicando su nombre y haciendo click en "Guardar".';
    if (nivel === 'cooperativa') {
      return {
        icon: env.images.icon.sede,
        eyebrow: 'Edición de Simbología',
        title: 'Mapa de comunidades',
        subtitle: subtitleInstructivo,
      };
    }
    if (nivel === 'localidad') {
      const nombreComunidad = location.state?.localidadNombre || 'Comunidad';
      return {
        icon: env.images.icon.comunidad,
        eyebrow: 'Edición de Simbología',
        title: `Mapa de ${nombreComunidad}`,
        subtitle: subtitleInstructivo,
      };
    }
    const nombreProductor = location.state?.productorNombre || 'Productor';
    return {
      icon: env.images.icon.user,
      eyebrow: 'Edición de Simbología Unidad de producción',
      title: `Mapa de ${nombreProductor}`,
      subtitle: subtitleInstructivo,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivel, location.state]);

  const [blocking, setBlocking] = useState(false);
  const [mapType, setMapType] = useState('hybrid');
  const [allGeoPuntos, setAllGeoPuntos] = useState([]);
  const [hiddenGeoIds, setHiddenGeoIds] = useState(new Set());
  const [tipo, setTipo] = useState('');
  const [nombre, setNombre] = useState('');
  const [selectedNombreCatalogo, setSelectedNombreCatalogo] = useState(0);
  const [color, setColor] = useState('#4A6700');
  const [formaLinea, setFormaLinea] = useState('solid');
  const [initialGeometry, setInitialGeometry] = useState(null);
  const [ready, setReady] = useState(false);
  const [catalogoNombres, setCatalogoNombres] = useState([]);
  const [coloresRecientes, setColoresRecientes] = useState(() => getColoresRecientes());
  const [dataLocalidades, setDataLocalidades] = useState([]);
  const [showComunidades, setShowComunidades] = useState(true);
  const idCicloActivo = coopConfig.config?.activeCicloSIC?.id;

  useEffect(() => {
    if (nivel !== 'cooperativa' || !idCicloActivo) return;
    Api.search('tx_padron_mapas', { filter: [{ column: 'id_ciclo', operator: '=', value: idCicloActivo }] })
      .then(response => setDataLocalidades(response.data || []))
      .catch(() => setDataLocalidades([]));
  }, [nivel, idCicloActivo]);

  const [dataParcelasLocalidad, setDataParcelasLocalidad] = useState([]);
  const [showParcelas, setShowParcelas] = useState(true);
  const idLocalidadOwner = location.state?.idLocalidad;

  useEffect(() => {
    if (nivel !== 'localidad' || !idLocalidadOwner || !idCicloActivo) return;
    Api.search('tx_padron_mapas_detalle', {
      filter: [
        { column: 'id_localidad', operator: '=', value: idLocalidadOwner },
        { column: 'id_ciclo', operator: '=', value: idCicloActivo },
      ],
    })
      .then(response => setDataParcelasLocalidad(response.data || []))
      .catch(() => setDataParcelasLocalidad([]));
  }, [nivel, idLocalidadOwner, idCicloActivo]);

  const [dataParcelasUnidad, setDataParcelasUnidad] = useState([]);
  const [showParcelasUnidad, setShowParcelasUnidad] = useState(true);

  useEffect(() => {
    if (nivel !== 'cuenta' || !ownerUuid || !idCicloActivo) return;
    Api.search('tx_padron_mapas_detalle', {
      filter: [
        { column: 'productor_uuid', operator: '=', value: ownerUuid },
        { column: 'id_ciclo', operator: '=', value: idCicloActivo },
      ],
    })
      .then(response => setDataParcelasUnidad(response.data || []))
      .catch(() => setDataParcelasUnidad([]));
  }, [nivel, ownerUuid, idCicloActivo]);

  const comunidadesEnMapa = useMemo(
    () =>
      dataLocalidades
        .filter(loc => isValidLat(loc.localidad_latitud) && isValidLng(loc.localidad_longitud))
        .map((loc, i) => ({
          label: loc.localidad,
          pos: { lat: Number(loc.localidad_latitud), lng: Number(loc.localidad_longitud) },
          badge: (
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: loc.cat_localidad_riesgo_color || env.colors.neutral_600,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 10,
                flexShrink: 0,
              }}>
              {i + 1}
            </span>
          ),
        })),
    [dataLocalidades]
  );

  const parcelasEnMapa = useMemo(() => {
    const porProductor = new Map();
    dataParcelasLocalidad
      .map(parcela => ({ ...parcela, _pos: getParcelaPosition(parcela) }))
      .filter(parcela => parcela._pos)
      .forEach(parcela => {
        if (porProductor.has(parcela.productor_uuid)) return;
        porProductor.set(parcela.productor_uuid, {
          label: parcela.productor_nombre,
          pos: parcela._pos,
          badge: (
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: parcela.cuenta_categoria_color || env.colors.primary_500,
                flexShrink: 0,
              }}
            />
          ),
        });
      });
    return Array.from(porProductor.values());
  }, [dataParcelasLocalidad]);

  const parcelasUnidadEnMapa = useMemo(
    () =>
      dataParcelasUnidad
        .map(parcela => ({ ...parcela, _pos: getParcelaPosition(parcela) }))
        .filter(parcela => parcela._pos)
        .map(parcela => ({
          label: parcela.parcela_nombre,
          pos: parcela._pos,
          badge: (
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: parcela.cuenta_categoria_color || env.colors.primary_500,
                flexShrink: 0,
              }}
            />
          ),
        })),
    [dataParcelasUnidad]
  );

  useEffect(() => {
    Api.getAll('cat_simbologia_nombres', { nivel: NIVEL_FILTRO_CATALOGO[nivel] })
      .then(response => setCatalogoNombres(response.data || []))
      .catch(() => setCatalogoNombres([]));
  }, [nivel]);

  const opcionesNombreTipo = useMemo(() => catalogoNombres.filter(c => c.tipo === tipo), [catalogoNombres, tipo]);

  const coloresUsados = useMemo(
    () =>
      buildUsedColors(allGeoPuntos, item =>
        item.tipo === 'punto' ? isCustomPuntoColor(item.color) : ['linea', 'area', 'poligono'].includes(item.tipo) && !!item.color
      ),
    [allGeoPuntos]
  );
  // El catálogo (nivel=parcela) ya trae su propio registro "Otro" para tipo
  // punto — si lo agregáramos de nuevo a mano, aparecería duplicado en el
  // select.
  const catalogoIncluyeOtro = useMemo(() => opcionesNombreTipo.some(o => o.nombre === NOMBRE_OTRO), [opcionesNombreTipo]);

  const parcelaDataForMap = useMemo(() => ({ centroid: anchor }), [anchor]);

  const visibleForMap = useMemo(() => allGeoPuntos.filter(p => !hiddenGeoIds.has(p.id) && (!isEditMode || p.uuid !== itemUuid)), [
    allGeoPuntos,
    hiddenGeoIds,
    itemUuid,
    isEditMode,
  ]);

  // Solo los puntos "Otro" tienen color editable (Casa, Escuela, etc. conservan el de su ícono).
  const esOtroPunto = tipo === 'punto' && selectedNombreCatalogo === NOMBRE_OTRO;
  // Color original del nombre elegido (catálogo, o el color base de "Otro") para ofrecerlo de vuelta en el selector de color.
  const colorOriginal = useMemo(() => {
    if (esOtroPunto) return { color: OTRO_PUNTO_DEFAULT_COLOR, label: null };
    const opcion = selectedNombreCatalogo !== 0 ? opcionesNombreTipo.find(o => o.nombre === selectedNombreCatalogo) : null;
    return opcion?.color_default ? { color: opcion.color_default, label: opcion.nombre } : { color: null, label: null };
  }, [esOtroPunto, selectedNombreCatalogo, opcionesNombreTipo]);
  // Color y tipo de línea dependen del elemento que se va a agregar: se muestran
  // hasta que ya se eligió su nombre.
  const nombreElegido = selectedNombreCatalogo !== 0;

  const { renderMap, center, zoom, drawnGeometry, map, maps } = useCroquisPuntosHook(
    parcelaDataForMap,
    [],
    tipo,
    ready ? initialGeometry : null,
    anchorZoom,
    color,
    formaLinea,
    nombre,
    esOtroPunto ? color : null
  );
  useGeoPuntosLayer(map, maps, visibleForMap);

  const basePath = () => {
    if (nivel === 'cooperativa') return '/admin/padron/mapas/editsimbologia/cooperativa';
    if (nivel === 'localidad') return `/admin/padron/mapas/editsimbologia/localidad/${ownerUuid}`;
    return `/admin/padron/mapas/editsimbologia/cuenta/${ownerUuid}`;
  };

  const listReturnUrl = () => {
    if (nivel === 'cooperativa') return '/admin/padron/mapas/list';
    const idLocalidad = location.state?.idLocalidad;
    if (nivel === 'localidad') return idLocalidad ? `/admin/padron/mapas/list?localidad=${idLocalidad}` : '/admin/padron/mapas/list';
    // nivel === 'cuenta'
    if (!idLocalidad) return '/admin/padron/mapas/list';
    return `/admin/padron/mapas/list?localidad=${idLocalidad}&productor=${ownerUuid}`;
  };

  const fetchList = () => {
    if (nivel === 'cooperativa') return Api.getAll(nivelConfig.apiPath);
    return Api.get(nivelConfig.apiPath, ownerUuid);
  };

  const createItem = data => {
    if (nivel === 'cooperativa') return Api.create(nivelConfig.apiPath, data);
    return Api.create(`${nivelConfig.apiPath}/${ownerUuid}`, data);
  };

  const updateItem = (uuid, data) => Api.update(nivelConfig.apiPath, uuid, data);
  const deleteItem = uuid => Api.delete(nivelConfig.apiPath, uuid);

  useEffect(() => {
    setTipo('');
    setNombre('');
    setSelectedNombreCatalogo(0);
    setColor('#4A6700');
    setFormaLinea('solid');
    setInitialGeometry(null);
    setReady(false);
    setAllGeoPuntos([]);
    setBlocking(true);

    fetchList()
      .then(response => {
        const items = response.data || [];
        setAllGeoPuntos(items);

        if (isEditMode) {
          const target = items.find(p => p.uuid === itemUuid);
          if (target) {
            setTipo(target.tipo);
            setNombre(target.nombre || '');
            if (target.tipo !== 'texto') {
              const esNombreCatalogo =
                catalogoNombres.some(c => c.tipo === target.tipo && c.nombre === target.nombre) && target.nombre !== NOMBRE_OTRO;
              setSelectedNombreCatalogo(esNombreCatalogo ? target.nombre : NOMBRE_OTRO);
            }
            setColor(
              target.tipo === 'punto'
                ? isCustomPuntoColor(target.color)
                  ? target.color
                  : OTRO_PUNTO_DEFAULT_COLOR
                : target.color || '#4A6700'
            );
            setFormaLinea(target.forma_linea || 'solid');
            if (target.tipo === 'punto' || target.tipo === 'texto') {
              setInitialGeometry({ lat: parseFloat(target.lat), lng: parseFloat(target.lng) });
            } else {
              setInitialGeometry(target.coordinates);
            }
          }
        }
      })
      .catch(() => setAllGeoPuntos([]))
      .finally(() => {
        setBlocking(false);
        setReady(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivel, ownerUuid, itemUuid]);

  // Posición live del mapa (no la de `location.state`, que es la de cuando se
  // entró a esta pantalla) — se usa al salir (Guardar/Cancelar/Volver) para no
  // perder el pan/zoom que el usuario haya hecho mientras editaba.
  const getLiveMapState = () => {
    if (!map) return null;
    const c = map.getCenter();
    return { center: { lat: c.lat(), lng: c.lng() }, zoom: map.getZoom() };
  };

  const navigateTo = (path, state) => {
    if (drawnGeometry) {
      Swal.fire({
        text: 'Si cambias a otro símbolo se perderán tus cambios ¿quieres continuar sin guardar?',
        showDenyButton: true,
        confirmButtonText: 'Seguir editando',
        denyButtonText: 'Continuar sin guardar',
      }).then(result => {
        if (result.isDenied) history.push(path, state);
      });
    } else {
      history.push(path, state);
    }
  };

  const handleBack = () => {
    const backState = getLiveMapState();
    if (drawnGeometry) {
      Swal.fire({
        text: 'Los cambios actuales se podrían perder. ¿Quieres salir sin guardar?',
        showDenyButton: true,
        confirmButtonText: 'Seguir editando',
        denyButtonText: 'Salir sin guardar',
      }).then(result => {
        if (result.isDenied) history.replace(listReturnUrl(), backState);
      });
    } else {
      history.replace(listReturnUrl(), backState);
    }
  };

  // Las opciones de Nombre dependen del tipo (punto/linea/area), así que un
  // nombre elegido para un tipo no tiene sentido al cambiar a otro.
  const handleTipoChange = value => {
    setTipo(value);
    setNombre('');
    setSelectedNombreCatalogo(0);
  };

  const handleNombreCatalogoChange = value => {
    setSelectedNombreCatalogo(value);
    if (value === 0 || value === NOMBRE_OTRO) {
      setNombre('');
      if (value === NOMBRE_OTRO && tipo === 'punto' && !isEditMode) setColor(OTRO_PUNTO_DEFAULT_COLOR);
      return;
    }
    setNombre(value);
    const opcion = opcionesNombreTipo.find(o => o.nombre === value);
    if (opcion?.color_default && !isEditMode) setColor(opcion.color_default);
  };

  const isSubmitDisabled = () => !drawnGeometry || !nombre.trim();

  const onSubmit = () => {
    const nombreFinal = nombre.trim();
    if (!drawnGeometry || !nombreFinal) return;
    setBlocking(true);
    const colorPayload = tipo === 'punto' && !esOtroPunto ? null : color;
    // Se captura antes de la llamada: el reset posterior al guardado no debe afectarlo.
    const colorOriginalGuardado = colorOriginal.color;
    const payload = {
      tipo,
      nombre: nombreFinal,
      color: colorPayload,
      forma_linea: tipo === 'linea' ? formaLinea : null,
      icono: null,
      geometria: drawnGeometry,
    };
    const apiCall = isEditMode ? updateItem(itemUuid, payload) : createItem(payload);

    apiCall
      .then(() => {
        setBlocking(false);
        // Recientes solo guarda colores que el usuario realmente eligió (distintos del original del catálogo).
        if (colorPayload && normalizeHex(colorPayload) !== normalizeHex(colorOriginalGuardado))
          setColoresRecientes(addColorReciente(colorPayload));
        Swal.fire({
          icon: 'success',
          title: '',
          text: isEditMode ? 'Cambios guardados correctamente' : 'Guardado correctamente',
          confirmButtonText: 'OK',
        }).then(result => {
          if (result.isConfirmed) {
            if (isEditMode) {
              history.push(basePath(), { ...location.state, ...getLiveMapState() });
            } else {
              // Sin reload: un reload remonta todo y relee `location.state`
              // (la posición de cuando se ENTRÓ a esta pantalla), perdiendo
              // el pan/zoom hecho mientras se armaba este símbolo nuevo.
              setTipo('');
              setNombre('');
              setSelectedNombreCatalogo(0);
              setColor('#4A6700');
              setFormaLinea('solid');
              fetchList()
                .then(response => setAllGeoPuntos(response.data || []))
                .catch(() => setAllGeoPuntos([]));
            }
          }
        });
      })
      .catch(error => {
        setBlocking(false);
        Swal.fire({ icon: 'error', title: '', text: error.response?.data?.message || 'Ocurrió un error al guardar' });
      });
  };

  const handleDelete = item => {
    const label = item.tipo === 'punto' ? 'punto' : item.tipo === 'texto' ? 'texto' : item.tipo === 'linea' ? 'línea' : 'área';
    Swal.fire({
      title: `¿Eliminar ${label}?`,
      text: `Se eliminará "${item.nombre || label}" permanentemente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#c0392b',
    }).then(result => {
      if (result.isConfirmed) {
        deleteItem(item.uuid)
          .then(() =>
            fetchList()
              .then(response => setAllGeoPuntos(response.data || []))
              .catch(() => setAllGeoPuntos([]))
          )
          .catch(() => Swal.fire({ icon: 'error', title: 'Error al eliminar' }));
      }
    });
  };

  const toggleHidden = id => {
    setHiddenGeoIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const panTo = pos => {
    if (map) map.panTo(pos);
  };

  const recenterMap = () => {
    const positions = [];
    if (nivel === 'cooperativa' && showComunidades) {
      dataLocalidades
        .filter(loc => isValidLat(loc.localidad_latitud) && isValidLng(loc.localidad_longitud))
        .forEach(loc => positions.push({ lat: Number(loc.localidad_latitud), lng: Number(loc.localidad_longitud) }));
    }
    if (nivel === 'localidad') {
      if (location.state?.localidadCentro) positions.push(location.state.localidadCentro);
      if (showParcelas) dataParcelasLocalidad.forEach(parcela => positions.push(getParcelaPosition(parcela)));
    }
    if (nivel === 'cuenta' && showParcelasUnidad) dataParcelasUnidad.forEach(parcela => positions.push(getParcelaPosition(parcela)));
    positions.push(...getGeoPuntoPositions(visibleForMap), ...getGeometryPositions(drawnGeometry));
    fitMapToPositions(map, maps, positions);
  };

  const coordsCenter = coords => ({
    lat: coords.reduce((s, c) => s + c.lat, 0) / coords.length,
    lng: coords.reduce((s, c) => s + c.lng, 0) / coords.length,
  });

  const getItemCenter = item => {
    if (item.tipo === 'punto' || item.tipo === 'texto') {
      const lat = parseFloat(item.lat);
      const lng = parseFloat(item.lng);
      return !isNaN(lat) && !isNaN(lng) ? { lat, lng } : null;
    }
    return Array.isArray(item.coordinates) && item.coordinates.length ? coordsCenter(item.coordinates) : null;
  };

  const pointsForDisplay = useMemo(
    () => allGeoPuntos.filter(p => p.tipo === 'punto').map((p, i) => ({ ...p, config: getPuntoConfig(p.nombre, i, p.color) })),
    [allGeoPuntos]
  );
  const linesForDisplay = useMemo(
    () =>
      allGeoPuntos
        .filter(p => p.tipo === 'linea')
        .map((p, i) => ({
          ...p,
          lineStyle: p.color ? { color: p.color, weight: 3, dashType: p.forma_linea || 'solid' } : LINE_STYLES[i % LINE_STYLES.length],
        })),
    [allGeoPuntos]
  );
  const polygonsForDisplay = useMemo(
    () =>
      allGeoPuntos
        .filter(p => p.tipo === 'area')
        .map((p, i) => ({ ...p, fill: p.color ? { fill: p.color, stroke: p.color } : getPolygonFill(i) })),
    [allGeoPuntos]
  );
  const textsForDisplay = useMemo(() => allGeoPuntos.filter(p => p.tipo === 'texto'), [allGeoPuntos]);

  const hasLegend = allGeoPuntos.length > 0;
  const hasLocalidadReference = nivel === 'localidad' && !!location.state?.localidadCentro;
  const activeItemUuid = isEditMode ? itemUuid : null;

  const hasSectionAfterPointsTexts = linesForDisplay.length > 0 || polygonsForDisplay.length > 0;
  const hasSectionAfterLines = polygonsForDisplay.length > 0;

  const SimboloActions = ({ item }) => (
    <div style={{ display: 'flex', gap: 10, flexShrink: 0, marginLeft: 'auto' }}>
      <i
        className="fa fa-pencil-alt"
        title="Editar"
        style={{
          color: item.uuid === activeItemUuid ? '#bbb' : '#507736',
          cursor: item.uuid === activeItemUuid ? 'default' : 'pointer',
          fontSize: 13,
        }}
        onClick={e => {
          e.stopPropagation();
          if (item.uuid === activeItemUuid) return;
          const itemCenter = getItemCenter(item);
          navigateTo(`${basePath()}/${item.uuid}`, { ...location.state, ...(itemCenter ? { center: itemCenter, zoom } : {}) });
        }}
      />
      <i
        className="fa fa-trash"
        title="Eliminar"
        style={{ color: '#c0392b', cursor: 'pointer', fontSize: 13 }}
        onClick={e => {
          e.stopPropagation();
          handleDelete(item);
        }}
      />
      <i
        className={hiddenGeoIds.has(item.id) ? 'fa fa-eye-slash' : 'fa fa-eye'}
        title={hiddenGeoIds.has(item.id) ? 'Mostrar en mapa' : 'Ocultar en mapa'}
        style={{ color: '#777', cursor: 'pointer', fontSize: 13 }}
        onClick={e => {
          e.stopPropagation();
          toggleHidden(item.id);
        }}
      />
    </div>
  );

  const SimboloItem = ({ children, onClick, isHidden }) => (
    <div
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', marginBottom: 6, cursor: 'pointer', opacity: isHidden ? 0.4 : 1 }}>
      {children}
    </div>
  );

  const activeTipoLabel = TIPO_OPTIONS.find(o => o.value === tipo)?.label || tipo;

  return (
    <>
      {/* Header row */}
      <div className="row justify-content-between mb-3 align-items-center">
        <button onClick={handleBack} type="button" className="btn btn-outline-secondary btn-sirio" style={{ borderRadius: 20 }}>
          <i className="fa fa-arrow-left mr-2" />
          Volver
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <FormSelectMaterial
            id="tipoMapa"
            error={false}
            label="Tipo Mapa"
            value={mapType}
            onChange={v => setMapType(v)}
            height={36}
            options={[
              { value: 'satellite', label: 'Satelital' },
              { value: 'terrain', label: 'Terreno' },
              { value: 'hybrid', label: 'Híbrido' },
            ]}
            showAll={false}
          />
        </div>
      </div>

      <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
        <CardContainer style={{ paddingTop: 16 }}>
          {/* Título + Guardar/Cancelar */}
          <div className="row mb-3">
            <div className="col-12">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    border: `1.5px solid ${env.colors.neutral_100}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    overflow: 'hidden',
                    background: '#fff',
                  }}>
                  <img src={nivelDisplay.icon} alt={nivelConfig.label} style={{ width: 26, height: 26 }} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: env.colors.neutral_700,
                    }}>
                    {nivelDisplay.eyebrow}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: env.colors.neutral_900 }}>{nivelDisplay.title}</div>
                  {nivelDisplay.subtitle && <div style={{ fontSize: 12, color: env.colors.neutral_700 }}>{nivelDisplay.subtitle}</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Inputs card */}
          <div
            style={{
              background: env.colors.neutral_2,
              border: `1px solid ${env.colors.neutral_100}`,
              borderRadius: 8,
              padding: '10px 18px',
              marginBottom: 14,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 16,
            }}>
            {/* Los campos viven en un contenedor que puede hacer wrap; los botones quedan fuera
                (sin wrap) para que Guardar siempre esté arriba a la derecha. */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 10 }}>
              {/* Group 1: mode label + tipo buttons — mismo patrón en línea que
                parcelas-edit-simbologia.js (label y botones en un solo renglón,
                no apilados). */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    color: env.colors.neutral_700,
                    marginRight: 4,
                  }}>
                  {isEditMode ? 'Editando' : 'Nuevo'}
                </span>
                {isEditMode ? (
                  <span style={tipoButtonStyle(true, false)}>
                    <i className={`fa ${TIPO_OPTIONS.find(o => o.value === tipo)?.icon || 'fa-map-marker-alt'}`} />
                    {activeTipoLabel}
                  </span>
                ) : (
                  TIPO_OPTIONS.map(({ value, label, icon }) => (
                    <button key={value} type="button" onClick={() => handleTipoChange(value)} style={tipoButtonStyle(tipo === value)}>
                      <i className={`fa ${icon}`} />
                      {label}
                    </button>
                  ))
                )}
              </div>

              <div
                style={{
                  width: 1,
                  alignSelf: 'stretch',
                  minHeight: 32,
                  backgroundColor: env.colors.neutral_100,
                  margin: '0 16px',
                  flexShrink: 0,
                }}
              />

              {/* Group 2: nombre + color + forma de línea — misma altura de una
                sola línea que el Grupo 1 (label en línea, no apilada arriba),
                para que "centrar" alinee de verdad todo el toolbar. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                {/* Sin tipo elegido no hay geometría que nombrar (y el catálogo,
                  filtrado por tipo, quedaría vacío salvo por "Otro") —
                  invitar a poner un nombre en ese momento no tiene sentido. */}
                {tipo && (
                  <>
                    <div style={{ minWidth: 200 }}>
                      {tipo === 'texto' ? (
                        // Mismo look que FormSelectMaterial (Select de MUI, variant
                        // "outlined" por default) — un TextField de MUI en vez del
                        // <input> plano de Bootstrap, para que las cuatro cajas del
                        // toolbar (Punto/Línea/Polígono usan el Select; Texto usa
                        // este input) se vean consistentes.
                        <TextField
                          id="nombreTexto"
                          label="Nombre"
                          placeholder="Texto en el mapa"
                          value={nombre}
                          onChange={e => setNombre(e.target.value)}
                          className="mt-1"
                          style={{ height: 36, minWidth: 160, fontSize: '14px' }}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ style: { height: 36, padding: '0 14px', fontSize: '14px', boxSizing: 'border-box' } }}
                        />
                      ) : (
                        // `cat_simbologia_nombres` es la fuente de opciones para
                        // punto/línea/polígono, pero "Otro" (mismo patrón que
                        // parcelas-edit-simbologia.js) permite un nombre libre cuando
                        // ninguna opción del catálogo aplica. `showAll` muestra el
                        // placeholder "Selecciona un nombre" con value=0 cuando no
                        // hay nada elegido, en vez de dejar la caja vacía.
                        <FormSelectMaterial
                          id="nombreCatalogo"
                          error={false}
                          label="Nombre"
                          value={selectedNombreCatalogo}
                          onChange={handleNombreCatalogoChange}
                          height={36}
                          options={[
                            ...opcionesNombreTipo.map(o => ({ value: o.nombre, label: o.nombre })),
                            ...(catalogoIncluyeOtro ? [] : [{ value: NOMBRE_OTRO, label: NOMBRE_OTRO }]),
                          ]}
                          showAll={true}
                          showAllLabel="Selecciona un nombre"
                        />
                      )}
                    </div>

                    {selectedNombreCatalogo === NOMBRE_OTRO && (
                      <TextField
                        id="nombreOtro"
                        label="Nombre"
                        placeholder="Escribe el nombre"
                        value={nombre}
                        onChange={e => setNombre(e.target.value)}
                        className="mt-1"
                        style={{ height: 36, minWidth: 160, fontSize: '14px' }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { height: 36, padding: '0 14px', fontSize: '14px', boxSizing: 'border-box' } }}
                      />
                    )}
                  </>
                )}

                {(((tipo === 'linea' || tipo === 'area') && nombreElegido) || esOtroPunto) && (
                  <SimbologiaColorPicker
                    value={color}
                    onChange={setColor}
                    usedColors={coloresUsados}
                    recentColors={coloresRecientes}
                    defaultColor={colorOriginal.color}
                    defaultLabel={colorOriginal.label}
                  />
                )}

                {tipo === 'linea' && nombreElegido && (
                  <div style={{ minWidth: 70 }}>
                    <FormControl fullWidth>
                      <InputLabel id="formaLinea-label" className="pr-1">
                        Tipo
                      </InputLabel>
                      <Select
                        labelId="formaLinea-label"
                        id="formaLinea"
                        value={formaLinea}
                        onChange={e => setFormaLinea(e.target.value)}
                        style={{ height: 36, minWidth: 70, fontSize: '14px' }}
                        className="mt-1"
                        label="Tipo"
                        renderValue={value => <LineSwatch style={{ color, dashType: value, weight: 2 }} />}>
                        {FORMA_LINEA_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            <LineSwatch style={{ color, dashType: option.value, weight: 2 }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
              {isEditMode && (
                <button
                  onClick={() => navigateTo(basePath(), { ...location.state, ...getLiveMapState() })}
                  type="button"
                  className="btn btn-outline-secondary btn-sirio"
                  style={{ borderRadius: 20, whiteSpace: 'nowrap', paddingLeft: 20, paddingRight: 20, flexShrink: 0 }}>
                  Cancelar
                </button>
              )}
              <button
                onClick={onSubmit}
                type="button"
                disabled={isSubmitDisabled()}
                className="btn btn-outline btn-sirio"
                style={{ borderRadius: 20, whiteSpace: 'nowrap', paddingLeft: 28, paddingRight: 28, flexShrink: 0 }}>
                Guardar
              </button>
            </div>
          </div>

          {/* Mapa */}
          <div style={{ position: 'relative', width: '100%', height: 480, marginBottom: 14 }}>
            <div className="map-rounded" style={{ height: '100%', width: '100%' }}>
              <NorthIcon color={mapType === 'terrain' ? 'black' : 'white'} />
              {ready && <MapRecenterButton onClick={recenterMap} />}
              {ready && (
                <GoogleMapReact
                  bootstrapURLKeys={{ key: env.googleApiKey, libraries: ['geometry'] }}
                  options={() => ({ mapTypeId: mapType })}
                  center={center}
                  zoom={zoom}
                  yesIWantToUseGoogleMapApiInternals
                  onGoogleApiLoaded={({ map, maps }) => renderMap(map, maps)}>
                  {nivel === 'cooperativa' &&
                    showComunidades &&
                    dataLocalidades
                      .filter(loc => isValidLat(loc.localidad_latitud) && isValidLng(loc.localidad_longitud))
                      .map((loc, i) => (
                        <CircleMarker
                          key={i}
                          lat={Number(loc.localidad_latitud)}
                          lng={Number(loc.localidad_longitud)}
                          number={i + 1}
                          color={loc.cat_localidad_riesgo_color}
                          title={loc.localidad}
                        />
                      ))}
                  {nivel === 'localidad' && location.state?.localidadCentro && (
                    <CircleMarker
                      lat={location.state.localidadCentro.lat}
                      lng={location.state.localidadCentro.lng}
                      number=""
                      color="#000"
                      size={14}
                      title={location.state?.localidadNombre || 'Comunidad'}
                    />
                  )}
                  {nivel === 'localidad' &&
                    showParcelas &&
                    dataParcelasLocalidad
                      .map(parcela => ({ ...parcela, _pos: getParcelaPosition(parcela) }))
                      .filter(parcela => parcela._pos)
                      .map((parcela, i) => (
                        <PillMarker
                          key={i}
                          lat={parcela._pos.lat}
                          lng={parcela._pos.lng}
                          label={parcela.productor_id}
                          color={parcela.cuenta_categoria_color}
                          title={`${parcela.productor_nombre} · ${parcela.parcela_nombre}`}
                        />
                      ))}
                  {nivel === 'cuenta' &&
                    showParcelasUnidad &&
                    dataParcelasUnidad
                      .map(parcela => ({ ...parcela, _pos: getParcelaPosition(parcela) }))
                      .filter(parcela => parcela._pos)
                      .map((parcela, i) => (
                        <PillMarker
                          key={i}
                          lat={parcela._pos.lat}
                          lng={parcela._pos.lng}
                          label={parcela.parcela_nombre}
                          color={parcela.cuenta_categoria_color}
                          title={parcela.parcela_nombre}
                        />
                      ))}
                </GoogleMapReact>
              )}
            </div>
          </div>

          {(hasLegend || isEditMode || hasLocalidadReference) && (
            <div
              style={{ border: '1px solid #c8d1a0', borderRadius: 8, padding: '12px 16px', backgroundColor: '#f9faf4', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#4A6700', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  Simbología
                </div>
              </div>

              <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>
                {(pointsForDisplay.length > 0 || textsForDisplay.length > 0 || hasLocalidadReference) && (
                  <div
                    style={{
                      flex: 1,
                      minWidth: 180,
                      paddingRight: hasSectionAfterPointsTexts ? 20 : 0,
                      borderRight: hasSectionAfterPointsTexts ? `1px solid ${env.colors.neutral_100}` : 'none',
                      marginBottom: 8,
                    }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 11,
                        color: '#666',
                        marginBottom: 8,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}>
                      Puntos / Textos
                    </div>
                    {hasLocalidadReference && (
                      <div
                        onClick={() => panTo(location.state.localidadCentro)}
                        style={{ display: 'flex', alignItems: 'center', marginBottom: 6, cursor: 'pointer' }}>
                        <PointSwatch color="#000" />
                        <span style={{ fontSize: 12, color: '#333' }}>{location.state?.localidadNombre || 'Comunidad'}</span>
                      </div>
                    )}
                    {pointsForDisplay.map(item => (
                      <SimboloItem
                        key={item.uuid}
                        onClick={() => panTo({ lat: parseFloat(item.lat), lng: parseFloat(item.lng) })}
                        isHidden={hiddenGeoIds.has(item.id)}>
                        <img
                          src={item.config.iconUrl}
                          alt={item.config.label}
                          style={{ width: 20, height: 20, marginRight: 6, flexShrink: 0 }}
                        />
                        <span style={{ fontSize: 12, color: '#333' }}>
                          {item.nombre || item.config.label}
                          {item.uuid === activeItemUuid && <em style={{ color: '#789D16', fontSize: 10 }}> (editando)</em>}
                        </span>
                        <SimboloActions item={item} />
                      </SimboloItem>
                    ))}
                    {textsForDisplay.map(item => (
                      <SimboloItem
                        key={item.uuid}
                        onClick={() => panTo({ lat: parseFloat(item.lat), lng: parseFloat(item.lng) })}
                        isHidden={hiddenGeoIds.has(item.id)}>
                        <TextSwatch />
                        <span style={{ fontSize: 12, color: '#333' }}>
                          {item.nombre || 'Texto'}
                          {item.uuid === activeItemUuid && <em style={{ color: '#789D16', fontSize: 10 }}> (editando)</em>}
                        </span>
                        <SimboloActions item={item} />
                      </SimboloItem>
                    ))}
                  </div>
                )}

                {linesForDisplay.length > 0 && (
                  <div
                    style={{
                      flex: 1,
                      minWidth: 180,
                      paddingLeft: pointsForDisplay.length > 0 || textsForDisplay.length > 0 ? 20 : 0,
                      paddingRight: hasSectionAfterLines ? 20 : 0,
                      borderRight: hasSectionAfterLines ? `1px solid ${env.colors.neutral_100}` : 'none',
                      marginBottom: 8,
                    }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 11,
                        color: '#666',
                        marginBottom: 8,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}>
                      Líneas
                    </div>
                    {linesForDisplay.map((item, i) => (
                      <SimboloItem
                        key={item.uuid}
                        onClick={() => Array.isArray(item.coordinates) && panTo(coordsCenter(item.coordinates))}
                        isHidden={hiddenGeoIds.has(item.id)}>
                        <LineSwatch style={item.lineStyle} />
                        <span style={{ fontSize: 12, color: '#333' }}>
                          {item.nombre || `Línea ${i + 1}`}
                          {item.uuid === activeItemUuid && <em style={{ color: '#789D16', fontSize: 10 }}> (editando)</em>}
                        </span>
                        <SimboloActions item={item} />
                      </SimboloItem>
                    ))}
                  </div>
                )}

                {polygonsForDisplay.length > 0 && (
                  <div
                    style={{
                      flex: 1,
                      minWidth: 180,
                      paddingLeft: pointsForDisplay.length > 0 || textsForDisplay.length > 0 || linesForDisplay.length > 0 ? 20 : 0,
                      marginBottom: 8,
                    }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 11,
                        color: '#666',
                        marginBottom: 8,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}>
                      Áreas
                    </div>
                    {polygonsForDisplay.map((item, i) => (
                      <SimboloItem
                        key={item.uuid}
                        onClick={() => Array.isArray(item.coordinates) && panTo(coordsCenter(item.coordinates))}
                        isHidden={hiddenGeoIds.has(item.id)}>
                        <AreaSwatch fill={item.fill} />
                        <span style={{ fontSize: 12, color: '#333' }}>
                          {item.nombre || `Área ${i + 1}`}
                          {item.uuid === activeItemUuid && <em style={{ color: '#789D16', fontSize: 10 }}> (editando)</em>}
                        </span>
                        <SimboloActions item={item} />
                      </SimboloItem>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {nivel === 'cooperativa' && (
            <ReferenceLegend
              title="Comunidades en el mapa"
              items={comunidadesEnMapa}
              panTo={panTo}
              show={showComunidades}
              onToggleShow={() => setShowComunidades(v => !v)}
            />
          )}
          {nivel === 'localidad' && (
            <ReferenceLegend
              title="Parcelas en el mapa"
              items={parcelasEnMapa}
              panTo={panTo}
              show={showParcelas}
              onToggleShow={() => setShowParcelas(v => !v)}
            />
          )}
          {nivel === 'cuenta' && (
            <ReferenceLegend
              title="Parcelas en el mapa"
              items={parcelasUnidadEnMapa}
              panTo={panTo}
              show={showParcelasUnidad}
              onToggleShow={() => setShowParcelasUnidad(v => !v)}
            />
          )}
        </CardContainer>
      </BlockUi>
    </>
  );
};

export default PadronMapasEditSimbologia;
